import { useQuery } from "@tanstack/react-query";

import { client } from "@/api/client";
import { t } from "@/i18n";
import type { components } from "@/api/schema";

export type CurrentUser = components["schemas"]["CurrentUser"];

export const meQueryKey = ["me"] as const;

/**
 * Who is signed in, what they may do, and what number they call from.
 *
 * One request at boot (.docs/04 section 4). `staleTime: Infinity` because none
 * of it changes without an admin action, and a background refetch storm on a
 * screen that renders conditionally on roles makes the UI flicker between
 * permission states.
 *
 * Deliberately no `retry`: a failure here is a 401, and `client`'s middleware
 * has already started navigating to the login page. Retrying would fire three
 * more requests at a server that has already said no.
 */
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      const { data, error } = await client.GET("/api/users/me/", {});
      if (error) throw new Error(t("errors.session"));
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** `app_label.codename`, the same strings `user.has_perm` takes on the server. */
export function can(user: CurrentUser | undefined, permission: string): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

export function hasRole(user: CurrentUser | undefined, role: string): boolean {
  return user?.roles?.includes(role) ?? false;
}
